import axios from "axios";

import cities from "@/assets/geo/cities.json";
import countyFips from "@/assets/geo/countyfips.json";
import cdFips from "@/assets/geo/cdfips.json";
import stateFips from "@/assets/geo/statefips.json";
import { geoJsonUrl } from "@/config";
import { storageKey } from "@/config";

import { feature } from "topojson-client";

const FETCH_CDS = "FETCH_CDS";
const FETCH_COUNTIES = "FETCH_COUNTIES";
const FETCH_CITIES = "FETCH_CITIES";
const FETCH_STATES = "FETCH_STATES";
const FETCH_SLDUS = "FETCH_SLDUS";
const FETCH_SLDLS = "FETCH_SLDLS";
const FETCH_VCUS = "FETCH_VCUS";
const FETCH_FIPS = "FETCH_FIPS";
const CONFIGURE_GEOJSON_API = "CONFIGURE_GEOJSON_API";
const PUT_TOPOJSON = "PUT_TOPOJSON";
const YEAR = process.env.VUE_APP_EMBED_MAP_VERSION;

const initialState = {
  api: {},
  cds: [],
  counties: [],
  cities: [],
  sldls: {},
  sldus: {},
  states: [],
  vcus: [],
  fips: {
    state: {},
    cd: {},
    county: {},
  },
};

const mutations = {
  [FETCH_CDS](state, cds) {
    state.cds = cds;
  },
  [FETCH_COUNTIES](state, counties) {
    state.counties = counties;
  },
  [FETCH_CITIES](state, cities) {
    state.cities = cities;
  },
  [FETCH_STATES](state, states) {
    state.states = states;
  },
  [FETCH_SLDUS](state, { stateName, sldu }) {
    state.sldus[stateName] = sldu;
  },
  [FETCH_SLDLS](state, { stateName, sldl }) {
    state.sldls[stateName] = sldl;
  },
  [FETCH_VCUS](state, { stateName, vcus }) {
    state.vcus[stateName] = vcus;
  },
  [FETCH_FIPS](state, fips) {
    state.fips = fips;
  },
  [CONFIGURE_GEOJSON_API](state, api) {
    state.api = api;
  },
  [PUT_TOPOJSON](state, { stateName, geoType, data }) {
    // temporary key until topojson refactoring is done
    const geoKey = {
      cd: "cds",
      sldu: "sldus",
      sldl: "sldls",
    };
    if (["sldl", "sldu"].includes(geoType)) {
      state[geoKey[geoType]][stateName] = data;
    } else {
      state[geoKey[geoType]] = data;
    }
  },
};

const localStorage = window.localStorage;
async function cachedFetch(api, route) {
  const lsKey = `${storageKey}-${encodeURIComponent(route)}`;

  if (localStorage.getItem(lsKey)) {
    return localStorage.getItem(lsKey);
  } else {
    let response = await api.get(route);
    localStorage.setItem(lsKey, JSON.stringify(response.data));
    return JSON.stringify(response.data);
  }
}

const actions = {
  configureGeoJsonAPI: ({ commit }) => {
    const config = {
      baseURL: geoJsonUrl,
    };
    const _axios = axios.create(config);
    _axios.interceptors.request.use(
      (config) => config,
      (error) => Promise.reject(error)
    );
    _axios.interceptors.response.use(
      (response) => response,
      (error) => Promise.reject(error)
    );
    commit(CONFIGURE_GEOJSON_API, _axios);
    return _axios;
  },
  async fetchCds({ commit, state, dispatch }) {
    let api = state.api;
    if (Object.keys(api).length === 0) {
      api = await dispatch("configureGeoJsonAPI");
    }
    try {
      let response = await api.get(`${YEAR}/Congressional Districts/Nation.json`);
      commit(FETCH_CDS, JSON.stringify(response.data));
    } catch (error) {
      dispatch("setError", error);
      throw error;
    }
  },
  async fetchCounties({ commit, state, dispatch }) {
    let api = state.api;
    if (Object.keys(api).length === 0) {
      api = await dispatch("configureGeoJsonAPI");
    }
    try {
      let response = await cachedFetch(api, `${YEAR}/counties.json`);
      commit(FETCH_COUNTIES, response);
    } catch (error) {
      dispatch("setError", error);
      throw error;
    }
  },
  fetchCities: ({ commit }) => {
    commit(FETCH_CITIES, JSON.stringify(cities));
  },
  async fetchStates({ commit, state, dispatch }) {
    let api = state.api;
    if (Object.keys(api).length === 0) {
      api = await dispatch("configureGeoJsonAPI");
    }
    try {
      let response = await api.get(`${YEAR}/states.json`);
      commit(FETCH_STATES, JSON.stringify(response.data));
    } catch (error) {
      dispatch("setError", error);
      throw error;
    }
  },
  async fetchVcus({ commit, state, dispatch }, states = []) {
    let api = state.api;
    if (Object.keys(api).length === 0) {
      api = await dispatch("configureGeoJsonAPI");
    }

    const promises = [];

    states.forEach((state) => {
      let response = cachedFetch(api, `${YEAR}/Towns/${state}.json`);

      response.then((data) => {
        commit(FETCH_VCUS, {
          stateName: state,
          vcus: data,
        });
      });

      promises.push(response);
    });

    await Promise.all(promises);
  },
  async fetchSldus({ commit, state, dispatch }, states = []) {
    let api = state.api;
    if (Object.keys(api).length === 0) {
      api = await dispatch("configureGeoJsonAPI");
    }

    const promises = [];

    states.forEach((state) => {
      let response = cachedFetch(api, `${YEAR}/State Senate/${state}.json`);

      response.then((data) => {
        commit(FETCH_SLDUS, { stateName: state, sldu: data });
      });

      promises.push(response);
    });

    await Promise.all(promises);
  },
  async fetchSldls({ commit, state, dispatch }, states = []) {
    let api = state.api;
    if (Object.keys(api).length === 0) {
      api = await dispatch("configureGeoJsonAPI");
    }

    const promises = [];

    states.forEach((state) => {
      let response = cachedFetch(api, `${YEAR}/State House/${state}.json`);

      response.then((data) => {
        commit(FETCH_SLDLS, { stateName: state, sldl: data });
      });

      promises.push(response);
    });

    await Promise.all(promises);
  },
  fetchFips: ({ commit }) => {
    commit(FETCH_FIPS, { state: stateFips, county: countyFips, cd: cdFips });
  },
  async fetchTopojson({ commit, state, dispatch }, url) {
    let api = state.api;
    if (Object.keys(api).length === 0) {
      api = await dispatch("configureGeoJsonAPI");
    }
    const data = await Promise.resolve(api.get(`${url}.json`));
    const parsedUrl = url.split("/");
    commit(PUT_TOPOJSON, {
      stateName: parsedUrl[2],
      geoType: parsedUrl[1],
      data: JSON.stringify(data.data),
    });
  },
};

const getters = {
  cds(state) {
    return state.cds;
  },
  counties(state) {
    return state.counties;
  },
  cities(state) {
    if (state.cities && state.cities.length > 0) {
      const cities = JSON.parse(state.cities);
      const data = feature(cities, cities.objects.cities);
      return data;
    }
    return [];
  },
  states(state) {
    return state.states;
  },
  sldls(state) {
    return state.sldls;
  },
  sldus(state) {
    return state.sldus;
  },
  vcus(state) {
    return state.vcus;
  },
  fips(state) {
    return state.fips;
  },
  missingFips(state) {
    return Object.values(state.fips).some((d) => {
      return Object.values(d).length === 0;
    });
  },
  topojson: (state) => (geoType, stateName) => {
    if (geoType === "state") {
      const states = JSON.parse(state.states);
      if (stateName)
        states.objects.states.geometries = states.objects.states.geometries.filter(
          (g) => g.properties.NAME === stateName
        );
      return () => feature(states, states.objects.states);
    }
    if (geoType === "cd") {
      let cds = JSON.parse(state.cds);
      if (stateName)
        cds.objects.cds.geometries = cds.objects.cds.geometries.filter(
          (g) => g.properties.NAME === stateName
        );
      return () => feature(cds, cds.objects.cds);
    }
    if (geoType === "county") {
      const counties = JSON.parse(state.counties);
      return () => feature(counties, counties.objects.counties);
    }
    if (geoType === "vcu") {
      const vcus = JSON.parse(state.vcus[stateName]);
      return () => feature(vcus, vcus.objects.vcu);
    }
    if (geoType === "sldu") {
      const sldus = JSON.parse(state.sldus[stateName]);
      return () => feature(sldus, sldus.objects.sldu);
    }
    if (geoType === "sldl") {
      const sldls = JSON.parse(state.sldls[stateName]);
      return () => feature(sldls, sldls.objects.sldl);
    }
  },
};

export default {
  namespaced: false,
  state: initialState,
  mutations,
  actions,
  getters,
};
